import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, takeUntil } from 'rxjs';
import { DestroyService, AlertService } from '@selfai-platform/shared';
import { Button } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { MessageModule } from 'primeng/message';
import { AccordionModule } from 'primeng/accordion';
import { TooltipModule } from 'primeng/tooltip';
import { ChatMessage, Agent, AgentType, PromptType } from '../../models';
import { AgentMappingsService } from '../../services';
import { ChatCodeViewerComponent } from '../chat-code-viewer';
import { CodeEditorRequestEvent, RunCommandResponse } from '../../types';

@Component({
  selector: 'selfai-platform-chat-messages-list',
  standalone: true,
  imports: [CommonModule, TranslateModule, Button, TagModule, MessageModule, AccordionModule, TooltipModule, ChatCodeViewerComponent],
  templateUrl: './chat-messages-list.component.html',
  styleUrls: ['./chat-messages-list.component.scss'],
  providers: [DestroyService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatMessagesListComponent implements OnInit, AfterViewChecked {
  @Input() messages$!: Observable<ChatMessage[]>;
  @Input() isProcessing$!: Observable<boolean>;
  @Input() selectedAgent: Agent | null = null;
  @Output() codeEditorRequested = new EventEmitter<CodeEditorRequestEvent>();

  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;

  private shouldScrollToBottom = false;
  private userScrolledUp = false;

  constructor(
    private readonly agentMappingsService: AgentMappingsService,
    private readonly cdr: ChangeDetectorRef,
    private readonly destroy$: DestroyService,
    private readonly translateService: TranslateService,
    private readonly alertService: AlertService
  ) {}

  public ngOnInit(): void {
    this.messages$?.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.userScrolledUp) {
        this.shouldScrollToBottom = true;
      }
      this.cdr.markForCheck();
    });
  }

  public ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }

    if (this.messagesContainer?.nativeElement && !this.messagesContainer.nativeElement.onscroll) {
      this.messagesContainer.nativeElement.onscroll = () => this.onScroll();
    }
  }

  public trackByMessage(_index: number, message: ChatMessage): string {
    return message.id;
  }

  public getAgentDisplayName(agentType?: AgentType): string {
    return this.agentMappingsService.getAgentDisplayName(agentType, this.selectedAgent);
  }

  public getAgentTypeLabel(agentType?: AgentType): string {
    return this.agentMappingsService.getAgentTypeLabel(agentType);
  }

  public getLanguageFromAgentType(agentType?: AgentType): string {
    return this.agentMappingsService.getLanguageFromAgentType(agentType);
  }

  public getMessageText(content: string): string {
    if (!content) return '';
    return content.replace(/```[\w]*\n([\s\S]*?)\n```/g, '').trim();
  }

  public parseJsonResponse(content: string): RunCommandResponse | null {
    if (!content || typeof content !== 'string' || content.trim().length === 0) {
      return null;
    }

    const trimmedContent = content.trim();
    if (!trimmedContent.startsWith('{') && !trimmedContent.startsWith('[')) {
      return null;
    }

    try {
      const parsed = JSON.parse(trimmedContent);
      if (parsed && (parsed.reply || parsed.plan || parsed.code || parsed.explanation)) {
        return parsed as RunCommandResponse;
      }
    } catch (e) {
      return null;
    }
    return null;
  }

  public isJsonResponse(content: string): boolean {
    return this.parseJsonResponse(content) !== null;
  }

  public getPromptTypeLabel(type: PromptType): string {
    return this.agentMappingsService.getPromptTypeLabel(type);
  }

  public openCodeInEditor(code: string): void {
    this.codeEditorRequested.emit({ code });
  }

  public copyCodeToClipboard(code: string): void {
    if (!code) return;

    navigator.clipboard
      .writeText(code)
      .catch((err) => {
        this.alertService.error(this.translateService.instant('chat.errors.failed-to-copy-code') + ':', err);
        this.fallbackCopyToClipboard(code);
      });
  }

  private fallbackCopyToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
    } catch (err) {
      this.alertService.error(this.translateService.instant('chat.errors.fallback-copy-failed') + ':', err);
    }

    document.body.removeChild(textArea);
  }

  private onScroll(): void {
    if (!this.messagesContainer?.nativeElement) return;

    const element = this.messagesContainer.nativeElement;
    const currentScrollTop = element.scrollTop;
    const maxScroll = element.scrollHeight - element.clientHeight;
    const threshold = 100;

    this.userScrolledUp = currentScrollTop < maxScroll - threshold;
  }

  private scrollToBottom(): void {
    try {
      if (this.messagesContainer?.nativeElement) {
        setTimeout(() => {
          const element = this.messagesContainer.nativeElement;
          element.scrollTo({
            top: element.scrollHeight,
            behavior: 'smooth'
          });
        }, 50);
      }
    } catch (err) {
      this.alertService.error(this.translateService.instant('chat.errors.error-scrolling-to-bottom') + ':', err);
    }
  }
}
